import { EmbeddedMessage } from 'constants/messageMediums';
import { selectIsCampaignAlertOverridesFeatureFlagOn } from 'core/userContext';
import isEmpty from 'lodash/isEmpty';
import { AlertsHistorySlideOver } from 'modules/campaigns/lib/components/AlertsHistorySlideOver';
import { ExperimentTakeover } from 'modules/experiments/takeover';
import { ExperimentTakeoverSource } from 'modules/experiments/takeover/constants';
import { DisplayPriorityProvider } from 'modules/placements/context/DisplayPriorityProvider';
import { DisplayPriorityTakeover } from 'modules/placements/takeovers';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { TemplateTakeoverContextProvider } from 'modules/templateEditor/integrations/InStudioTemplateEditing/context';
import { CampaignEditorTemplateContextProvider } from 'modules/templateEditor/integrations/context/CampaignEditorTemplateContextProvider';
import { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Spinner } from 'ui/spinner';
import { NoOp } from 'utils/noop';
import { AlertTakeover, DisplayUntilTakeover } from '../lib/takeovers';
import { CampaignEditorStep } from './CampaignEditorStep';
import { CampaignNotFoundError } from './components/CampaignEditorError';
import {
  CampaignAlertsProvider,
  CampaignProvider,
  EditorProvider,
  FeatureFlagContextProvider,
  ModalProvider,
  TemplateProvider,
  UnsavedChangesPromptProvider,
  useCampaignAlertsContext,
  useCampaignContext,
  useEditorContext,
} from './context';

import { Modals } from './modals';
import { AudienceTakeover } from './takeovers';

import { CampaignAlertHistorySource } from '../lib/components/AlertsHistorySlideOver/types';
import { useSaveCampaign } from './hooks';
import { CampaignEditorFormState, CampaignStep } from './types';

export const ExperimentTakeoverComp = ({ onClose }: { onClose: NoOp }) => {
  const { locale, setLocale } = useQueryParamContext();
  return (
    <ExperimentTakeover
      source={ExperimentTakeoverSource.CAMPAIGN_EDITOR}
      onClose={onClose}
      currentLocale={locale}
      setCurrentLocale={setLocale}
    />
  );
};

export const CampaignEditorCmp = () => {
  // We are going to get the id of the campaign right away and hydrate the template state context provider, there was a race condition where the value kept returning to 0 even after populated
  // this will be fixed and removed once we unify the template and campaign contexts for shared data access.;
  const [loading, setLoading] = useState(true);
  const { isLoading: isCampaignLoading, campaign, refetch: campaignRefetch } = useCampaignContext();
  const {
    viewCampaignStep,
    isDisplayPriorityTakeoverOpen,
    isDisplayUntilTakeoverOpen,
    setIsDisplayPriorityTakeoverOpen,
    setIsDisplayUntilTakeoverOpen,
  } = useEditorContext();
  const { campaignData, isAlertTakeoverOpen, onTakeoverOpen } = useCampaignAlertsContext();
  const { isAlertHistorySlideOverOpen, setIsAlertHistorySlideOverOpen } = useEditorContext();

  const { saveCampaign } = useSaveCampaign();

  /**
   * Alert takeover
   */
  const isCampaignAlertOverridesEnabled = useSelector(selectIsCampaignAlertOverridesFeatureFlagOn);

  const isCampaignEmpty = isEmpty(campaign);
  const refetchCampaign = () => {
    if (isCampaignEmpty) campaignRefetch();
  };

  useEffect(() => {
    if (loading && !isCampaignEmpty) {
      setLoading(false);
    }
  }, [isCampaignEmpty]);

  const isLoading = loading && isCampaignLoading;

  // this provider runs a lot of queries and hooks that aren't needed for non embedded campaign editors
  // we should not render in that case for non embedded campaigns
  const ConditionalDisplayProviderCmp =
    campaign.messageMedium === EmbeddedMessage ? DisplayPriorityProvider : Fragment;

  if (!isLoading && isCampaignEmpty) {
    return <CampaignNotFoundError retry={refetchCampaign} />;
  }

  if (isLoading) return <Spinner />;

  return (
    <CampaignEditorTemplateContextProvider
      campaignId={campaign.id}
      templateId={campaign.templateId}
    >
      <TemplateTakeoverContextProvider>
        <TemplateProvider>
          <UnsavedChangesPromptProvider>
            <ModalProvider>
              <FeatureFlagContextProvider>
                <ConditionalDisplayProviderCmp campaign={campaign}>
                  <CampaignEditorStep />
                  <ExperimentTakeoverComp
                    onClose={() => {
                      campaignRefetch();
                      viewCampaignStep(CampaignStep.ConversionAndExperiment);
                    }}
                  />
                  <AudienceTakeover />
                  {isDisplayPriorityTakeoverOpen && (
                    <DisplayPriorityTakeover
                      onClose={() => setIsDisplayPriorityTakeoverOpen(false)}
                      currentCampaignId={campaign.id}
                    />
                  )}
                  {isDisplayUntilTakeoverOpen && (
                    <DisplayUntilTakeover
                      onSave={(displayUntil) => {
                        saveCampaign({ displayUntil } as unknown as CampaignEditorFormState, () => {
                          campaignRefetch();
                          setIsDisplayUntilTakeoverOpen(false);
                        });
                      }}
                      onClose={() => setIsDisplayUntilTakeoverOpen(false)}
                      displayUntil={campaign.displayUntil}
                    />
                  )}
                  {isCampaignAlertOverridesEnabled && isAlertTakeoverOpen && (
                    <AlertTakeover campaignData={campaignData} onTakeoverOpen={onTakeoverOpen} />
                  )}

                  {isAlertHistorySlideOverOpen && (
                    <AlertsHistorySlideOver
                      onClose={() => !isAlertTakeoverOpen && setIsAlertHistorySlideOverOpen(false)}
                      campaignId={campaign.id}
                      templateId={campaign.templateId}
                      onEditAlerts={() => {
                        onTakeoverOpen(true);
                      }}
                      campaignName={campaign.name}
                      campaignType={campaign.campaignTypeDefinition}
                      messageMedium={campaign.messageMedium}
                      campaignState={campaign.campaignState}
                      campaignAlertsEnabled={!!campaign?.campaignAlertsEnabled}
                      source={CampaignAlertHistorySource.CampaignEditor}
                    />
                  )}
                  <Modals />
                </ConditionalDisplayProviderCmp>
              </FeatureFlagContextProvider>
            </ModalProvider>
          </UnsavedChangesPromptProvider>
        </TemplateProvider>
      </TemplateTakeoverContextProvider>
    </CampaignEditorTemplateContextProvider>
  );
};

export const CampaignEditor = () => (
  <CampaignProvider>
    <EditorProvider>
      <CampaignAlertsProvider>
        <CampaignEditorCmp />
      </CampaignAlertsProvider>
    </EditorProvider>
  </CampaignProvider>
);
